import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import Header from '../components/Header';
import Footer from '../components/Footer';

import {
  Buildings,
  CheckCircle,
  CircleNotch,
  UserPlus,
  XCircle
} from '@phosphor-icons/react';
const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  // Check if user is logged in and fetch invite details
  useEffect(() => {
    const checkAuthAndFetchInvite = async () => {
      if (!token) {
        setError('Invalid invitation link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        // Check if user is logged in
        const authResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (authResponse.ok) {
          const userData = await authResponse.json();
          setIsLoggedIn(true);
          setCurrentUserEmail(userData.email);
        }

        // Fetch invite details (public endpoint)
        const inviteResponse = await fetch(`${BACKEND_URL}/api/org/invite-details?token=${token}`);
        
        if (inviteResponse.ok) {
          const data = await inviteResponse.json();
          setInviteDetails(data);
        } else {
          const errorData = await inviteResponse.json();
          setError(errorData.detail || 'Invalid or expired invitation');
        }
      } catch (err) {
        console.error('Error checking invitation:', err);
        setError('Failed to load invitation details');
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndFetchInvite();
  }, [token]);

  const handleAcceptInvite = async () => {
    if (!isLoggedIn) {
      // Redirect to login with return URL
      const returnUrl = `/accept-invite?token=${token}&email=${email}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}&email=${encodeURIComponent(email)}`);
      return;
    }

    // Check if logged-in user matches invite email
    if (currentUserEmail !== inviteDetails?.email) {
      toast.error(`This invitation is for ${inviteDetails?.email}. Please log in with that account.`);
      return;
    }

    try {
      setAccepting(true);
      
      const response = await fetch(`${BACKEND_URL}/api/org/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const data = await response.json();
        setAccepted(true);
        toast.success(data.message);
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to accept invitation');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error('An error occurred while accepting the invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <CircleNotch className="w-8 h-8 text-[#6366F1] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#161B26]">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-md mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Invalid Invitation</h1>
              <p className="text-[#A0AEC0] mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E3] transition-colors"
              >
                Go to Login
              </button>
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-[#161B26]">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-md mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass rounded-2xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Welcome!</h1>
              <p className="text-[#A0AEC0] mb-2">
                You've been added to <strong className="text-white">{inviteDetails?.organization_name}</strong>
              </p>
              <p className="text-[#718096] text-sm mb-6">
                Redirecting you to the dashboard...
              </p>
              <CircleNotch className="w-6 h-6 text-[#6366F1] animate-spin mx-auto" />
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26]">
      <Header />
      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-8"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#6366F1]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <UserPlus className="w-8 h-8 text-[#6366F1]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Organization Invitation</h1>
              <p className="text-[#A0AEC0]">You've been invited to join an organization</p>
            </div>

            <div className="bg-[#1A1B1E] rounded-xl p-6 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#6366F1] to-[#EC4899] rounded-xl flex items-center justify-center">
                  <Buildings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {inviteDetails?.organization_name}
                  </h3>
                  <p className="text-[#718096] text-sm">
                    Role: <span className="text-[#A0AEC0] capitalize">{inviteDetails?.role}</span>
                  </p>
                </div>
              </div>

              {inviteDetails?.invited_by && (
                <p className="text-[#718096] text-sm">
                  Invited by: <span className="text-[#A0AEC0]">{inviteDetails.invited_by}</span>
                </p>
              )}

              <p className="text-[#718096] text-sm mt-2">
                Invitation for: <span className="text-[#A0AEC0]">{inviteDetails?.email}</span>
              </p>
            </div>

            {!isLoggedIn && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                <p className="text-yellow-500 text-sm">
                  Please log in with <strong>{inviteDetails?.email}</strong> to accept this invitation.
                </p>
              </div>
            )}

            {isLoggedIn && currentUserEmail !== inviteDetails?.email && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
                <p className="text-red-400 text-sm">
                  You're logged in as <strong>{currentUserEmail}</strong>, but this invitation is for <strong>{inviteDetails?.email}</strong>.
                  Please log out and log in with the correct account.
                </p>
              </div>
            )}

            <button
              onClick={handleAcceptInvite}
              disabled={accepting || (isLoggedIn && currentUserEmail !== inviteDetails?.email)}
              className="w-full py-3 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {accepting ? (
                <>
                  <CircleNotch className="w-5 h-5 animate-spin" />
                  Accepting...
                </>
              ) : !isLoggedIn ? (
                'Log In to Accept'
              ) : (
                'Accept Invitation'
              )}
            </button>

            <p className="text-center text-[#718096] text-xs mt-4">
              By accepting, you'll be added to this organization and can switch between your organizations from the sidebar.
            </p>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AcceptInvite;
